import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { fetchCampaignListsAndCompany } from "src/services/EngagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  registerAudienceListLabels,
  toAudienceListOptions,
  type AudienceListOption,
} from "src/utils/audienceListLabels";
import { COMPANY_NAME_STORAGE_KEY } from "./CampaignSetupConstants";

export const useAudienceListsAndCompany = (
  onOptionsLoaded?: (_options: AudienceListOption[]) => void
) => {
  const { i18n } = useTranslation();
  const [audienceOptions, setAudienceOptions] = useState<AudienceListOption[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [isListsLoading, setIsListsLoading] = useState(true);
  const onOptionsLoadedRef = useRef(onOptionsLoaded);

  useEffect(() => {
    onOptionsLoadedRef.current = onOptionsLoaded;
  }, [onOptionsLoaded]);

  useEffect(() => {
    let isActive = true;
    const tenantId = localStorage.getItem("tenant_id");
    const apiKey = localStorage.getItem("api_key");

    const load = async () => {
      if (!tenantId || !apiKey) {
        setIsListsLoading(false);
        return;
      }

      try {
        const data = await fetchCampaignListsAndCompany(
          tenantId,
          apiKey,
          getApiLangFromI18n(i18n)
        );
        if (!isActive) {
          return;
        }

        const options = registerAudienceListLabels(toAudienceListOptions(data));
        setAudienceOptions(options);
        onOptionsLoadedRef.current?.(options);

        if (data.company_name_engagements) {
          setCompanyName(data.company_name_engagements);
          localStorage.setItem(COMPANY_NAME_STORAGE_KEY, data.company_name_engagements);
        }
      } catch {
        if (isActive) {
          message.error(i18n.t("campaignFlow.createCampaignNew.listsLoadFailed"));
        }
      } finally {
        if (isActive) {
          setIsListsLoading(false);
        }
      }
    };

    void load();

    return () => {
      isActive = false;
    };
  }, [i18n]);

  return { audienceOptions, companyName, isListsLoading };
};
